import React, { useState, useEffect } from 'react';
import { EmotionChip } from '@/components/EmotionChip';
// { useEffect, useMemo, useState } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CE_COLORS = {
  "CE-01": "var(--ce-01-romance)",
  "CE-02": "var(--ce-02-joy)",
  "CE-03": "var(--ce-03-sorrow)",
  "CE-04": "var(--ce-04-fury)",
  "CE-05": "var(--ce-08-valor)",
  "CE-06": "var(--ce-05-wonder)",
  "CE-07": "var(--ce-06-dread)",
  "CE-08": "var(--ce-07-revulsion)",
  "CE-09": "var(--ce-09-serenity)",
};

const CE_NAMES = {
  "CE-01": "Romance",
  "CE-02": "Joy",
  "CE-03": "Sorrow",
  "CE-04": "Fury",
  "CE-05": "Valor",
  "CE-06": "Dread",
  "CE-07": "Revulsion",
  "CE-08": "Wonder",
  "CE-09": "Serenity",
};

function normalizeEpisodes(episodes = []) {
  return episodes.map((episode) => ({
    ...episode,
    primary_ce: episode.primary_ce || "CE-01",
    emotional_intensity: episode.emotional_intensity || 5,
  }));
}

function getTransitionState(fromEpisode, toEpisode, derivations) {
  const primary = derivations.primary?.find((rule) => rule.from === fromEpisode.primary_ce && rule.to === toEpisode.primary_ce);
  const secondary = derivations.secondary?.find((rule) => rule.from === fromEpisode.primary_ce && rule.to === toEpisode.primary_ce);
  const forbidden = derivations.forbidden?.find((rule) => rule.from === fromEpisode.primary_ce && rule.to === toEpisode.primary_ce);

  if (primary || secondary) {
    const rule = primary || secondary;
    return {
      status: "natural",
      color: "var(--ce-08-valor)",
      label: "✓ Natural flow",
      note: rule.description || "This emotional turn is supported by the taxonomy.",
    };
  }

  if (forbidden) {
    return {
      status: "warning",
      color: "var(--ce-02-joy)",
      label: "⚠ Intermediate beat needed",
      note: forbidden.solution || forbidden.reason || "Add an intermediate emotional bridge before this transition.",
    };
  }

  return {
    status: "open",
    color: "var(--ink-secondary)",
    label: "↗ Flexible bridge",
    note: "This jump is not explicitly mapped. Consider adding a transitional beat if it feels abrupt.",
  };
}

function CustomDot({ cx, cy, payload }) {
  const color = CE_COLORS[payload.primary_ce] || "var(--accent-gold)";
  const size = 6 + (payload.scene_count || 1) * 2;

  return (
    <g data-testid={`arc-planner-node-${payload.id || payload.episode_number}`}>
      <circle cx={cx} cy={cy} r={size} fill={color} fillOpacity={0.28} />
      <circle cx={cx} cy={cy} r={size / 2} fill={color} />
      {payload.is_paywall_gate && (
        <text x={cx} y={cy - size - 4} textAnchor="middle" fill="var(--ce-02-joy)" fontSize={10}>
          💰
        </text>
      )}
    </g>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const episode = payload[0].payload;
  const color = CE_COLORS[episode.primary_ce] || "var(--accent-gold)";

  return (
    <div
      className="rounded-xl p-3 shadow-2xl text-xs"
      data-testid="arc-planner-tooltip"
      style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)", minWidth: 180 }}
    >
      <div className="font-bold mb-1">Ep {episode.episode_number}: {episode.title}</div>
      <EmotionChip ceCode={episode.primary_ce} size="sm" />
      <div className="text-[var(--ink-secondary)]">Intensity: {episode.emotional_intensity}/10</div>
      <div className="text-[var(--ink-secondary)]">{episode.scene_count} scenes</div>
      {episode.is_paywall_gate && <div className="text-[var(--semantic-warning)]">💰 Paywall Gate</div>}
    </div>
  );
}

export default function ArcPlannerView({ projectId, episodes: propEpisodes }) {
  const [arcData, setArcData] = useState([]);
  const [derivations, setDerivations] = useState({ primary: [], secondary: [], forbidden: [] });
  const [loading, setLoading] = useState(true);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const derivationRequest = axios.get(`${API}/arcengine/taxonomy/derivations`, { withCredentials: true });

        if (projectId) {
          const [arcResponse, derivationResponse] = await Promise.all([
            axios.get(`${API}/projects/${projectId}/arc`, { withCredentials: true }),
            derivationRequest,
          ]);
          if (cancelled) return;
          setArcData(normalizeEpisodes(arcResponse.data));
          setDerivations(derivationResponse.data || { primary: [], secondary: [], forbidden: [] });
        } else {
          const derivationResponse = await derivationRequest;
          if (cancelled) return;
          setArcData(normalizeEpisodes(propEpisodes || []));
          setDerivations(derivationResponse.data || { primary: [], secondary: [], forbidden: [] });
        }
      } catch {
        if (!cancelled) {
          setArcData(normalizeEpisodes(propEpisodes || []));
          setDerivations({ primary: [], secondary: [], forbidden: [] });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, propEpisodes]);

  useEffect(() => {
    setChartReady(false);
    const timer = setTimeout(() => setChartReady(true), 60);
    return () => clearTimeout(timer);
  }, [loading, arcData.length]);

  const paywallEpisodes = arcData.filter((episode) => episode.is_paywall_gate);
  const avgIntensity = arcData.length
    ? (arcData.reduce((sum, episode) => sum + (episode.emotional_intensity || 5), 0) / arcData.length).toFixed(1)
    : "0.0";

  const dominantCE = arcData.reduce((accumulator, episode) => {
    accumulator[episode.primary_ce] = (accumulator[episode.primary_ce] || 0) + 1;
    return accumulator;
  }, {});

  const topCE = Object.entries(dominantCE).sort((a, b) => b[1] - a[1])[0]?.[0];

  const transitions = useMemo(
    () => arcData.slice(0, -1).map((episode, index) => ({
      from: episode,
      to: arcData[index + 1],
      ...getTransitionState(episode, arcData[index + 1], derivations),
    })),
    [arcData, derivations]
  );

  const episodeNumbers = arcData.map((episode) => episode.episode_number);
  const minEpisode = episodeNumbers.length ? Math.min(...episodeNumbers) : 1;
  const maxEpisode = episodeNumbers.length ? Math.max(...episodeNumbers) : 1;
  const xDomain = arcData.length === 1
    ? [Math.max(1, minEpisode - 0.5), maxEpisode + 0.5]
    : [Math.max(1, minEpisode - 0.5), maxEpisode + 0.5];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="arc-planner-loading">
        <div className="w-8 h-8 rounded-full border-2 border-[var(--accent-gold)] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (arcData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="arc-planner-empty-state">
        <div className="text-[var(--ink-tertiary)] text-sm">Add episodes to see your emotional arc</div>
      </div>
    );
  }

  return (
    <div data-testid="arc-planner-page">
      <h2 className="font-bold text-xl mb-2">Arc Planner</h2>
      <p className="text-[var(--ink-secondary)] text-sm mb-6">Emotional intensity across your series timeline</p>

      <div
        className="rounded-xl p-4 mb-6"
        data-testid="arc-planner-chart-card"
        style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", height: 280 }}
      >
        {chartReady ? (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <XAxis
                type="number"
                dataKey="episode_number"
                name="Episode"
                tickLine={false}
                tick={{ fill: "var(--ink-tertiary)", fontSize: 11 }}
                domain={xDomain}
                ticks={episodeNumbers}
                allowDecimals={false}
                label={{ value: "Episodes", position: "insideBottom", fill: "var(--ink-tertiary)", fontSize: 11, offset: -5 }}
              />
              <YAxis
                type="number"
                dataKey="emotional_intensity"
                name="Intensity"
                domain={[0, 10]}
                tickLine={false}
                tick={{ fill: "var(--ink-tertiary)", fontSize: 11 }}
                label={{ value: "Intensity", angle: -90, position: "insideLeft", fill: "var(--ink-tertiary)", fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              {paywallEpisodes.map((episode) => (
                <ReferenceLine key={episode.id} x={episode.episode_number} stroke="var(--ce-02-joy)" strokeDasharray="4 4" strokeWidth={1.5} />
              ))}
              <Scatter data={arcData} shape={<CustomDot />} />
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full w-full animate-pulse rounded-xl" style={{ background: "var(--surface-page)" }} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="arc-planner-stats-strip">
        {[
          { label: "Total Episodes", value: arcData.length },
          { label: "Avg Intensity", value: `${avgIntensity}/10` },
          { label: "Dominant Emotion", value: topCE ? <EmotionChip ceCode={topCE} size="md" /> : "—", color: "var(--ink-primary)" },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-xl p-4 text-center"
            data-testid={`arc-planner-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
          >
            <div className="text-xl font-black" style={{ color: stat.color || "#fff" }}>{stat.value}</div>
            <div className="text-[var(--ink-tertiary)] text-xs mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {transitions.length > 0 && (
        <div
          className="rounded-xl p-4 mb-6"
          data-testid="arc-planner-derivation-strip"
          style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
        >
          <div className="text-[var(--ink-secondary)] text-xs font-semibold mb-3">Episode-to-Episode Derivations</div>
          <div className="space-y-3">
            {transitions.map((transition) => (
              <div
                key={`${transition.from.id}-${transition.to.id}`}
                className="rounded-xl p-3"
                data-testid={`derivation-transition-${transition.from.episode_number}-${transition.to.episode_number}`}
                style={{ background: "var(--surface-page)", border: `1px solid ${transition.color}40` }}
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold">Ep {transition.from.episode_number}</span>
                    <EmotionChip ceCode={transition.from.primary_ce} size="sm" />
                    <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: `${transition.color}18`, color: transition.color }}>
                      {transition.label}
                    </span>
                    <span className="text-xs font-semibold">Ep {transition.to.episode_number}</span>
                    <EmotionChip ceCode={transition.to.primary_ce} size="sm" />
                  </div>
                  <p className="text-[var(--ink-secondary)] text-xs leading-relaxed">{transition.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl p-4" data-testid="arc-planner-legend-card" style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
        <div className="text-[var(--ink-secondary)] text-xs font-semibold mb-3">Emotion Legend</div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(CE_NAMES).map(([code, name]) => {
            const count = dominantCE[code] || 0;
            if (count === 0) return null;
            return (
              <div key={code} className="flex items-center gap-1.5" data-testid={`arc-planner-legend-${code.toLowerCase()}`}>
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: CE_COLORS[code] }} />
                <span className="text-[var(--ink-secondary)] text-xs">{name}</span>
                <span className="text-[var(--ink-tertiary)] text-xs">({count})</span>
              </div>
            );
          })}
        </div>
        {paywallEpisodes.length > 0 && (
          <div className="flex items-center gap-1.5 mt-3" data-testid="arc-planner-paywall-legend">
            <div className="w-8 h-0 border-t border-dashed border-[var(--semantic-warning)]" />
            <span className="text-[var(--semantic-warning)] text-xs">Paywall Gate</span>
          </div>
        )}
      </div>
    </div>
  );
}